/**
 * 刷新一批实体所属槽口/集合宿主的派生视觉（上色轮廓）。
 *
 * 设计说明：
 * 1. 槽口/集合的派生视觉必须跟随几何，而几何写者不止绘制/拖拽一条路——参数求解写回
 *    （改尺寸、松手裁决）与 Undo/Redo 恢复同样改几何，**必须走同一条刷新路**；
 *    此前刷新只挂在绘制/拖拽上，漏掉的路就在屏幕上残留旧形状。
 * 2. 槽口中心线已实体化（直槽口构造中心线直线、圆弧槽口构造中心线弧），
 *    随槽口参数更新链路自行同步几何，不再存在独立显示层，因此本函数只负责上色轮廓。
 * 3. 按鸭子类型反查宿主并调用宿主自身的刷新方法：本函数被 solver 写回层与
 *    历史恢复层共用，不 import mesh 类以避免循环依赖。
 *
 * @param entities 几何发生变化的实体集合（边/点均可，沿祖先链识别槽口/集合宿主）。
 */
export function refreshSlotOwnersDerivedVisuals(entities: Iterable<unknown>): void {
    const owners = new Map<string, object>();
    const registerOwner = (candidate: unknown): boolean => {
        if (!candidate || typeof candidate !== 'object') {
            return false;
        }
        if (
            Reflect.get(candidate, 'isSketchSlotObject') === true
            || Reflect.get(candidate, 'isSketchCollectionObject') === true
        ) {
            owners.set(String(Reflect.get(candidate, 'uuid')), candidate);
            return true;
        }

        return false;
    };

    for (const entity of entities) {
        /**
         * 集合（矩形/多边形）宿主由成员边的 `closedCollection` 直连；
         * 槽口成员既可能挂 closedCollection 也可能只在祖先链上，双路都查。
         * 曾只认槽口——参数写回后矩形的上色轮廓残留旧形状（边框已到位、
         * 填充还是老样子），用户看到的就是"形状错乱、拖一下又好了"。
         */
        if (entity && typeof entity === 'object') {
            registerOwner(Reflect.get(entity, 'closedCollection'));
        }

        let ancestor: unknown = entity;

        while (ancestor && typeof ancestor === 'object') {
            if (registerOwner(ancestor)) {
                break;
            }
            ancestor = Reflect.get(ancestor, 'parent');
        }
    }

    owners.forEach((owner) => {
        const refreshContour = Reflect.get(owner, 'refreshContourGeometry');

        if (typeof refreshContour === 'function') {
            refreshContour.call(owner);
        }
    });
}
